import React, { useMemo, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

export default function WarrantyReportsTable({ items, totalCount, loading, lazyParams, onPage, columns, actionColumn, tableKey = 'warranty-reports' }) {
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const normalized = useMemo(() => (Array.isArray(items) ? items : []), [items]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalized;

        const isDateKey = (key) => /date/i.test(key);
        return [...normalized].sort((a, b) => {
            let aVal = a?.[sortField];
            let bVal = b?.[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortOrder;
            if (bVal == null) return -sortOrder;

            const looksLikeDate = isDateKey(sortField) || (typeof aVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(aVal)) || (typeof bVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(bVal)) || aVal instanceof Date || bVal instanceof Date;

            if (looksLikeDate) {
                const da = aVal instanceof Date ? aVal : new Date(aVal);
                const db = bVal instanceof Date ? bVal : new Date(bVal);
                return sortOrder * (da.getTime() - db.getTime());
            }

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortOrder * (aVal - bVal);
            }

            return sortOrder * String(aVal).localeCompare(String(bVal), 'tr', { numeric: true, sensitivity: 'base' });
        });
    }, [normalized, sortField, sortOrder]);

    const handleSort = useCallback((e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    }, []);

    const label = (k) =>
        k
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (s) => s.toUpperCase())
            .trim();

    const dateFields = useMemo(() => {
        const set = new Set();
        (columns || []).forEach((c) => {
            if (c?.dataType === 'date' || /date/i.test(c?.field)) set.add(c.field);
        });
        return set;
    }, [columns]);

    const formatDateOnly = (val) => {
        const d = typeof val === 'string' ? new Date(val) : val instanceof Date ? val : null;
        if (!d || isNaN(d.getTime())) return '';
        return d.toLocaleDateString('tr-TR');
    };

    const cell = (row, key) => {
        const v = row?.[key];
        if (v == null) return '';
        if (dateFields.has(key)) return formatDateOnly(v);
        if (typeof v === 'number') return new Intl.NumberFormat().format(v);
        if (typeof v === 'string') {
            if (/^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}:\d{2})?/.test(v)) {
                const d = new Date(v);
                if (!isNaN(d.getTime())) return d.toLocaleString();
            }
            return v;
        }
        if (v instanceof Date) return formatDateOnly(v);
        return String(v);
    };

    const actionBody = (row) => (
        <Button
            icon="pi pi-pencil"
            className="p-button-text p-0 bg-transparent border-0 shadow-none hover:bg-transparent focus:bg-transparent"
            onClick={() => actionColumn?.onAction?.(row)}
            tooltip="Detay"
            tooltipOptions={{ position: 'left' }}
            aria-label="Detay"
            pt={{
                icon: {
                    className: 'cursor-pointer text-blue-500 hover:text-blue-700',
                    style: { fontSize: '1.1rem' }
                }
            }}
        />
    );

    const prefColumns = useMemo(() => {
        const cols = (columns || []).map((c) => ({
            field: c.field,
            columnKey: c.field,
            header: c.header ?? label(c.field),
            sortable: true,
            body: c.body ? (row) => c.body(row) : (row) => cell(row, c.field)
        }));

        if (actionColumn) {
            cols.unshift({
                field: '__actions',
                columnKey: '__actions',
                lockedFromPrefs: true,
                lockedPosition: 'start',
                header: actionColumn.header ?? '',
                body: actionBody,
                style: { width: '4rem', textAlign: 'center' }
            });
        }

        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, actionColumn, dateFields]);

    return (
        <StickyScrollTable deps={[sortedData, columns]} scrollBarPosition="top">
            <PreferencesDataTable
                tableKey={tableKey}
                columns={prefColumns}
                value={sortedData}
                dataKey="Id"
                lazy
                paginator
                first={lazyParams.first || 0}
                rows={lazyParams.rows || 10}
                totalRecords={totalCount}
                onPage={onPage}
                loading={loading}
                onSort={handleSort}
                sortField={sortField || undefined}
                sortOrder={sortOrder || 0}
                sortMode="single"
                rowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
                showGridlines
                className="p-datatable-gridlines"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                emptyMessage="No record found."
                tableStyle={{ minWidth: '50rem' }}
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
        </StickyScrollTable>
    );
}
